import React from "react";
import ProofAvatar from "./../../widget/public/shared/ProofAvatar";
import ProofText from "./../../widget/public/shared/ProofText";
import Stars from "./../../widget/public/shared/Stars";
import useContainerColumns from "./../../widget/public/shared/useContainerColumns";
import PoweredByShosay from "./PoweredByShosay";
import WallHero, { heroFields } from "./WallHero";
import LoadMoreSentinel from "./../../widget/public/shared/LoadMoreSentinel";
import { widgetPalette, withAlpha } from "./../../widget/public/shared/colorUtils";
import { datesEnabled, formatProofDateShort } from "./../../widget/public/shared/proofDate";

const COLUMN_BREAKPOINTS = [
  { min: 1280, cols: 3 },
  { min: 768,  cols: 2 },
  { min: 0,    cols: 1 },
];

const distribute = (proofs, cols) => {
  const buckets = Array.from({ length: cols }, () => []);
  (proofs || []).forEach((p, i) => buckets[i % cols].push(p));
  return buckets;
};

export const Futuristic = ({ wall, proofs, loaded, loadMore, loadingMore, exhausted }) => {
  const [cols, setRef] = useContainerColumns(COLUMN_BREAKPOINTS, 1);
  // The theme is designed dark, but bg_color is user-set and the editor
  // writes a near-white default, which left light grey text on a light
  // canvas. Derive the text and card colours from whatever the background
  // actually is so the neon look survives on either.
  const palette = widgetPalette({ ...wall, bg_color: wall.bg_color || "#05060A" });
  const bg = palette.bg;
  const accent = wall.primary_color || "#00F0FF"; // cyan
  const isDark = palette.text === "#ffffff";
  const showDate = datesEnabled(wall);
  const intro = wall.intro || "";
  const heroOn = heroFields(wall).enabled;
  const buckets = distribute(proofs, cols);

  return (
    <div
      ref={setRef}
      className="min-h-screen w-full relative overflow-hidden"
      style={{ backgroundColor: bg, color: palette.text, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
    >
      {/* Subtle scanline grid backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          color: accent,
        }}
      />
      {/* Glow blob */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ backgroundColor: accent }}
      />

      {heroOn && (
        <header className="relative px-6 sm:px-10 pt-16 sm:pt-24 pb-12 max-w-6xl mx-auto">
          <div className="text-[11px] font-bold uppercase tracking-[0.4em]" style={{ color: accent }}>
            // {wall.name}
          </div>
          <WallHero
            wall={wall}
            accent={accent}
            headingClassName="text-4xl sm:text-6xl font-black leading-[1.05] mt-4 max-w-3xl tracking-tight"
            headingStyle={{ fontFamily: "inherit" }}
            subtextClassName="mt-4 text-base sm:text-lg leading-relaxed opacity-70 max-w-2xl whitespace-pre-line"
            ctaWrapClassName="mt-7"
            ctaClassName="inline-flex items-center justify-center px-6 py-3 font-bold text-sm uppercase tracking-[0.2em] no-underline border"
          />
          <div
            className="mt-6 inline-block text-[10px] font-bold tracking-[0.3em] uppercase px-3 py-1.5 border"
            style={{ borderColor: accent, color: accent }}
          >
            {proofs.length} testimonial{proofs.length === 1 ? "" : "s"} · live feed
          </div>
        </header>
      )}

      {!loaded && (
        <div className="text-center py-12 relative" style={{ color: palette.muted }}>[ booting feed ]</div>
      )}

      <main className={`relative px-6 sm:px-10 pb-20 max-w-6xl mx-auto ${heroOn ? "" : "pt-16 sm:pt-24"}`}>
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {buckets.map((bucket, ci) => (
            <div key={`col-${ci}`} className="flex flex-col gap-4">
              {bucket.map((proof) => (
                <NeonCard key={proof.uuid} proof={proof} accent={accent} palette={palette} isDark={isDark} showDate={showDate} />
              ))}
            </div>
          ))}
        </div>
        {loadMore && (
          <LoadMoreSentinel
            onVisible={loadMore}
            loading={loadingMore}
            exhausted={exhausted}
            color={wall.primary_color || "#3E8DFF"}
          />
        )}
      </main>

      <div className="relative">
        <PoweredByShosay />
      </div>
    </div>
  );
};

const NeonCard = ({ proof, accent, palette, isDark, showDate }) => {
  return (
    <article
      className="relative p-5 transition-all hover:-translate-y-0.5"
      style={{
        backgroundColor: palette.cardBgSet
          ? palette.cardBg
          : isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.75)",
        border: `1px solid ${withAlpha(accent, isDark ? 0.2 : 0.45)}`,
        boxShadow: isDark
          ? `0 0 24px ${accent}22, inset 0 0 0 1px rgba(255,255,255,0.04)`
          : `0 0 18px ${accent}18, inset 0 0 0 1px rgba(15,23,42,0.06)`,
        backdropFilter: "blur(2px)",
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <ProofAvatar proof={proof} size={36} ringColor={accent} />
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm truncate" style={{ color: palette.cardText }}>
            {proof.name || "anonymous"}
          </div>
          {proof.contact_title && (
            <div className="text-[10px] font-bold opacity-60 truncate">
              {proof.contact_title}
              {proof.contact_company_name ? ` · ${proof.contact_company_name}` : ""}
            </div>
          )}
        </div>
        {showDate && formatProofDateShort(proof.given_at_utc) && (
          <div className="text-[10px] font-bold opacity-50 tabular-nums whitespace-nowrap">
            {formatProofDateShort(proof.given_at_utc)}
          </div>
        )}
      </div>

      {proof.rating != null && (
        <div className="mb-2"><Stars rating={proof.rating} color={accent} /></div>
      )}

      {proof.headline && (
        <div className="font-black text-base mb-2 leading-tight" style={{ color: palette.cardText }}>
          {proof.headline}
        </div>
      )}
      <ProofText proof={proof} className="text-sm leading-relaxed opacity-90" style={{ color: palette.cardText }} />
    </article>
  );
};

export default Futuristic;
